/**
 * `app:startup_breakdown` — per-stage startup timing, fired once per launch
 * right alongside `app:startup_ready` (same moment; purely additive — the
 * frozen `app:startup_ready` payload/timing is untouched).
 *
 * Fleet p90 on `app:startup_ready` is ~12s with no visibility into WHERE the
 * time goes. This event breaks that single number into stages spanning both
 * processes:
 *
 *   - mainBootMs: process start -> `app.whenReady` (main process boot).
 *   - windowCreateMs: `app.whenReady` -> BrowserWindow 'ready-to-show'.
 *   - loadMs: 'ready-to-show' -> webContents 'did-finish-load' (bundle
 *     download + parse, from the main process's point of view).
 *   - safeStorageWarmMs: wall-clock time of the `warmSafeStorage()` keychain
 *     probe fired at ready-to-show — the macOS keychain-stall suspect.
 *   - rendererBootMs: renderer `performance.timeOrigin` -> end of
 *     `src/main.tsx` module execution (bundle parse + top-level init).
 *   - connectionsLoadedMs: renderer origin -> connection store `loading`
 *     flips false.
 *   - splashHiddenMs: renderer origin -> App's `appReady` flips true (splash
 *     screen hidden). Currently fires at nearly the same instant as
 *     connectionsLoadedMs (the splash-min-time gate was removed), but kept as
 *     a separate mark in case that changes again.
 *
 * Main-side timestamps travel to the renderer via the
 * `analytics.getStartupTimings()` bridge (electron/main.ts's
 * 'app:get-startup-timings' IPC handler); the renderer computes durations
 * with `computeStartupBreakdown` below.
 *
 * Missing-value convention: every duration is either a clamped non-negative
 * integer or the sentinel `UNKNOWN_DURATION_MS` (-1) when the underlying
 * timestamp hasn't been captured yet (e.g. queried before a later stage has
 * happened, or `process.getCreationTime()` returned null). We always send the
 * key with this sentinel rather than omitting it, so the event keeps a stable
 * property shape on the dashboard.
 */

/** Sentinel for "this stage's timestamp wasn't available when queried." */
export const UNKNOWN_DURATION_MS = -1;

export interface MainStartupTimings {
  /** `process.getCreationTime()` (ms epoch), or null if unavailable. */
  processCreationTimeMs: number | null;
  /** Fallback anchor: `Date.now()` captured at the top of main.ts module evaluation. */
  moduleLoadMs: number;
  /** `Date.now()` captured inside the `app.whenReady().then()` handler, or null if not reached yet. */
  whenReadyMs: number | null;
  /** `Date.now()` captured at BrowserWindow 'ready-to-show', or null if not reached yet. */
  windowReadyToShowMs: number | null;
  /** `Date.now()` captured at webContents 'did-finish-load', or null if not reached yet. */
  didFinishLoadMs: number | null;
  /** Wall-clock ms the `warmSafeStorage()` call took, or null if it hasn't run yet. */
  safeStorageWarmMs: number | null;
}

export interface RendererStartupMarks {
  /**
   * The renderer-start anchor, in the SAME clock domain as the marks below —
   * for `performance.now()` marks that is 0 (see `RENDERER_PERF_ORIGIN_MS`),
   * never the epoch-valued `performance.timeOrigin`.
   */
  rendererOriginMs: number;
  /** `performance.now()` at the end of `src/main.tsx` module execution, or null if not yet captured. */
  bundleExecEndMs: number | null;
  /** `performance.now()` when the connection store's `loading` flipped false, or null if not yet. */
  connectionsLoadedAtMs: number | null;
  /** `performance.now()` when App's `appReady` flipped true, or null if not yet. */
  splashHiddenAtMs: number | null;
}

export interface StartupBreakdownProps {
  mainBootMs: number;
  windowCreateMs: number;
  loadMs: number;
  safeStorageWarmMs: number;
  rendererBootMs: number;
  connectionsLoadedMs: number;
  splashHiddenMs: number;
  platform: string;
}

const clampDuration = (ms: number): number => (ms < 0 ? 0 : Math.round(ms));

/** Duration between two optional epoch/perf timestamps, or UNKNOWN_DURATION_MS if either is missing. */
const durationOrUnknown = (startMs: number | null, endMs: number | null): number =>
  startMs === null || endMs === null ? UNKNOWN_DURATION_MS : clampDuration(endMs - startMs);

/**
 * Pure mapping from raw main + renderer timestamps to the event's property
 * object. Never throws.
 */
export function computeStartupBreakdown(
  main: MainStartupTimings,
  renderer: RendererStartupMarks,
  platform: string,
): StartupBreakdownProps {
  const mainBootAnchorMs = main.processCreationTimeMs ?? main.moduleLoadMs;

  return {
    mainBootMs: durationOrUnknown(mainBootAnchorMs, main.whenReadyMs),
    windowCreateMs: durationOrUnknown(main.whenReadyMs, main.windowReadyToShowMs),
    loadMs: durationOrUnknown(main.windowReadyToShowMs, main.didFinishLoadMs),
    safeStorageWarmMs:
      main.safeStorageWarmMs === null ? UNKNOWN_DURATION_MS : clampDuration(main.safeStorageWarmMs),
    rendererBootMs: durationOrUnknown(renderer.rendererOriginMs, renderer.bundleExecEndMs),
    connectionsLoadedMs: durationOrUnknown(renderer.rendererOriginMs, renderer.connectionsLoadedAtMs),
    splashHiddenMs: durationOrUnknown(renderer.rendererOriginMs, renderer.splashHiddenAtMs),
    platform,
  };
}

/**
 * The renderer origin in the `performance.now()` clock domain. `now()` counts
 * milliseconds FROM `performance.timeOrigin`, so the origin itself is 0 there;
 * `timeOrigin` is an epoch timestamp (~1.7e12) and subtracting it from a mark
 * went negative and clamped to 0 — 1.5.0 reported 0 for every renderer stage.
 * Probed in Electron 34 / Chromium 132: `Date.now() - (timeOrigin + now())`
 * is under 1 ms, i.e. `now()` really is relative to `timeOrigin`.
 */
const RENDERER_PERF_ORIGIN_MS = 0;

let bundleExecEndMs: number | null = null;
let connectionsLoadedAtMs: number | null = null;
let splashHiddenAtMs: number | null = null;

/** Call once at the end of `src/main.tsx` module execution. Idempotent. */
export function markRendererBundleExecEnd(): void {
  if (bundleExecEndMs === null) {
    bundleExecEndMs = performance.now();
  }
}

/** Call once when the connection store's `loading` flips false. Idempotent. */
export function markConnectionsLoaded(): void {
  if (connectionsLoadedAtMs === null) {
    connectionsLoadedAtMs = performance.now();
  }
}

/** Call once when App's `appReady` flips true (splash hidden). Idempotent. */
export function markSplashHidden(): void {
  if (splashHiddenAtMs === null) {
    splashHiddenAtMs = performance.now();
  }
}

let fired = false;

/**
 * Fetches the main-side timings and fires `app:startup_breakdown`. Fires at
 * most once per launch. Never throws — a failure here must never affect
 * startup or the (frozen) `app:startup_ready` event.
 */
export function reportStartupBreakdown(): void {
  if (fired) return;
  fired = true;

  const emit = () => {
    void (async () => {
      try {
        const timings = await window.electronAPI?.analytics?.getStartupTimings?.();
        if (!timings) return;

        const platform = window.electronAPI?.windowChrome?.platform ?? 'unknown';
        const props = computeStartupBreakdown(
          timings,
          {
            rendererOriginMs: RENDERER_PERF_ORIGIN_MS,
            bundleExecEndMs,
            connectionsLoadedAtMs,
            splashHiddenAtMs,
          },
          platform,
        );

        await window.electronAPI?.analytics?.track('app:startup_breakdown', { ...props });
      } catch {
        // Never throw — telemetry must not affect startup.
      }
    })();
  };

  try {
    // Deferred past first paint (double rAF + a capped idle callback), same
    // pattern as `reportStartupReady` — this event must add zero cost to the
    // path leading to first paint.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => emit(), { timeout: 10_000 });
        } else {
          setTimeout(emit, 0);
        }
      });
    });
  } catch {
    // Never throw — telemetry must not affect startup.
  }
}

/** Test-only: reset the once-per-window state. */
export function _resetStartupBreakdownForTests(): void {
  fired = false;
  bundleExecEndMs = null;
  connectionsLoadedAtMs = null;
  splashHiddenAtMs = null;
}
